import { useEffect } from 'react'
import { refreshChatsSidebar } from '@/lib/chats-sidebar-events'
import { useAgentPanelWorkspaceGuard } from '@/hooks/use-agent-panel-workspace-guard'
import { useChatSurfaceRegistry } from '@/hooks/use-chat-surface-registry'

/** Connects window events to ChatSurfaceRegistry methods inside the provider. */
export function ChatSurfaceRegistryBridge() {
  const registry = useChatSurfaceRegistry()
  useAgentPanelWorkspaceGuard()

  useEffect(() => {
    const moveHandler = (e: Event) => {
      const detail = (e as CustomEvent<{
        threadId?: string
        target?: 'agent-panel' | 'editor'
        openInNewTab?: boolean
      }>).detail ?? {}
      if (!detail.threadId) return
      if (detail.target === 'agent-panel') {
        registry.moveToAgentPanel(detail.threadId)
      } else if (detail.target === 'editor') {
        registry.moveToEditorTab(detail.threadId, { openInNewTab: detail.openInNewTab })
      }
    }
    const threadCreatedHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ fromThreadId?: string; toThreadId?: string; title?: string }>).detail ?? {}
      if (detail.fromThreadId && detail.toThreadId) {
        registry.migrateThreadId(detail.fromThreadId, detail.toThreadId, detail.title)
        refreshChatsSidebar()
      }
    }
    const newChatHandler = (e: Event) => {
      const detail = (e as CustomEvent<{
        contextDocumentId?: string
        contextDocumentTitle?: string
        forceAgentPanel?: boolean
        threadId?: string
        threadTitle?: string
        preferNew?: boolean
      }>).detail ?? {}
      registry.createNewChat({
        forceAgentPanel: detail.forceAgentPanel ?? true,
        contextDocumentId: detail.contextDocumentId,
        contextDocumentTitle: detail.contextDocumentTitle,
        threadId: detail.threadId,
        threadTitle: detail.threadTitle,
        preferNew: detail.preferNew,
      })
    }
    const tabUserClosedHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ tabId?: string }>).detail ?? {}
      if (detail.tabId) {
        registry.closeEditorTab(detail.tabId)
      }
    }
    const editorBindingSyncHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string }>).detail ?? {}
      if (detail.threadId) {
        registry.ensureEditorBinding(detail.threadId)
      }
    }
    window.addEventListener('chat-create-new', newChatHandler)
    window.addEventListener('chat-surface-move', moveHandler as EventListener)
    window.addEventListener('chat-thread-migrate', threadCreatedHandler as EventListener)
    window.addEventListener('chat-conversation-tab-user-closed', tabUserClosedHandler as EventListener)
    window.addEventListener('chat-editor-binding-sync', editorBindingSyncHandler as EventListener)
    return () => {
      window.removeEventListener('chat-create-new', newChatHandler)
      window.removeEventListener('chat-surface-move', moveHandler as EventListener)
      window.removeEventListener('chat-thread-migrate', threadCreatedHandler as EventListener)
      window.removeEventListener('chat-conversation-tab-user-closed', tabUserClosedHandler as EventListener)
      window.removeEventListener('chat-editor-binding-sync', editorBindingSyncHandler as EventListener)
    }
  }, [registry])

  return null
}
